import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@material-ui/core';

// UTILITY
import {
  analyticsChooseSearchItem,
  analyticsLeftSearch,
  analyticsNoSearchResults,
  analyticsSearched,
} from '@/utility/analytics';

// HELP
import { geocodeByPlaceId } from '@/utility/geocode';

// STYLES
import { AutocompleteWrapper } from './styles';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

const apiKey = process.env.REACT_APP_GOOGLE_KEY;

const AddressAutoComplete = ({ onAddressChange, inputProps, onClose, analyticsLocation }) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const itemWasSelected = React.useRef(false);
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}&language=en&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }

  const fetch = useMemo(() => {
    return debounce((request, callback) => {
      setLoading(true);
      autocompleteService.current.getPlacePredictions(request, callback);
    }, 500);
  }, [autocompleteService]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window?.google?.maps) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, results => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue]);

  useEffect(() => {
    if (!loading && !options.length) {
      analyticsNoSearchResults({ query: inputValue, search_location: analyticsLocation });
    }
  }, [loading]);

  const getUniqueItemsLength = () => {
    let uniqueItems = options.map(option => option.place_id);
    uniqueItems = [...new Set(uniqueItems)];
    return uniqueItems.length;
  };

  useEffect(() => {
    if (options.length) {
      const numberOfItemsDisplayed = getUniqueItemsLength();

      const properties = {
        query: inputValue,
        numberOfItemsDisplayed,
        first_item_type: 'address',
        first_item_name: options[0].description,
        first_item_id: options[0].place_id,
        search_location: analyticsLocation,
      };

      analyticsSearched(properties);
    }
  }, [options]);

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'select-option') {
      itemWasSelected.current = true;
    } else if (reason === 'clear') {
      itemWasSelected.current = false;
    }

    const numberOfItemsDisplayed = getUniqueItemsLength();

    setValue(newValue);

    if (newValue) {
      geocodeByPlaceId(newValue.place_id).then(res => {
        const address = res[0];

        address.description = newValue.description;
        address.structured_formatting = newValue.structured_formatting;

        const index = options.findIndex(place => place.place_id === newValue.place_id);

        const properties = {
          query: inputValue,
          numberOfItemsDisplayed,
          type: 'address',
          title: newValue.description,
          id: newValue.place_id,
          positionOfItem: index + 1,
          search_location: analyticsLocation,
        };

        analyticsChooseSearchItem(properties);

        onAddressChange(address);
      });
    } else {
      onAddressChange(newValue);
    }
  };

  const handleChange = (event, newInputValue) => {
    if (itemWasSelected.current) {
      itemWasSelected.current = false;
    }

    setInputValue(newInputValue);
  };

  const handleOpen = () => (itemWasSelected.current = false);

  const handleClose = (e, reason) => {
    if (reason === 'blur' && !itemWasSelected.current) {
      const numberOfResults = getUniqueItemsLength();

      const properties = {
        query: inputValue,
        numberOfResults,
        search_location: analyticsLocation,
      };

      analyticsLeftSearch(properties);

      if (itemWasSelected.current) itemWasSelected.current = false;
    }

    onClose(e, reason);
  };

  return (
    <AutocompleteWrapper>
      <Autocomplete
        getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
        filterOptions={x => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={handleInputChange}
        onInputChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        renderOption={option => {
          const matches = option.structured_formatting.main_text_matched_substrings;
          const parts = parse(
            option.structured_formatting.main_text,
            matches.map(match => [match.offset, match.offset + match.length]),
          );

          return (
            <Grid container alignItems="center">
              <Grid item>
                <LocationOnIcon className={classes.icon} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="textSecondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          );
        }}
        renderInput={params => (
          <TextField {...params} {...inputProps} placeholder={t('inputs.addressPlaceholder')} />
        )}
      />
    </AutocompleteWrapper>
  );
};

AddressAutoComplete.defaultProps = {
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  onAddressChange: () => {},
  inputProps: {},
  analyticsLocation: '',
};

AddressAutoComplete.propTypes = {
  loading: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onAddressChange: PropTypes.func,
  inputProps: PropTypes.object,
  analyticsLocation: PropTypes.string,
};

export default React.memo(AddressAutoComplete);
